// ── THE FARM'S 2026 ARC/PLC ELECTION SUMMARY — ONE PAGE, EVERY COVERED COMMODITY ─────────
//
// Kevin, 2026-09-19: one summary per FARMER, not one sheet per crop and county — corn,
// soybeans and wheat on one page a grower can read in ten seconds, with the evidence under
// each crop. Two facts shape it:
//
//   · We will usually NOT know the farm's FSA PLC payment yield or its base acres. Base acres
//     only size the dollars — both programs pay on the same 85% of the same base acres, so they
//     cancel out of the comparison (arcPlcElection.js). The payment yield does NOT cancel, so an
//     unknown one is answered with the DIVIDING LINE, never with a number of our own.
//   · A single "above X bu PLC, below X bu ARC-CO" exists only where the arithmetic has one.
//     Where ARC-CO starts paying at a higher price than PLC (corn and soybeans in 2026) no
//     payment yield decides it at every price, and the page says what IS true instead: ARC-CO
//     pays first as prices fall, and PLC is larger only below a price line set by the payment
//     yield — shown at a few ruler yields, which are marks on a ruler and never the farm's yield.
//
// ⚠ THIS MODULE CALCULATES NOTHING OF ITS OWN. Every figure is the engine's —
// resolvePracticeRow / countyRead / enrolRead / electionOutlook in ./arcPlcElection and the
// cap and payment-acre constants in ./arcPlcRule — the SAME calls the Coverage Advisor and the
// Full Strategy Report make, so the three surfaces cannot price one county three ways. What it
// adds is assembly: which crops and counties, in what order, labelled how.
//
// ⚠ AND IT ONLY STATES WHAT THE ARITHMETIC SUPPORTS. A result is a MODELED COMPARISON at a
// stated county yield and the FSA/USDA figures of a stated date, never an election made for
// the grower. An agency recommendation, if one is ever printed, is a separate human input.
import { resolvePracticeRow, countyRead, enrolRead, BELOW_SWITCH_ARC, belowSwitchArc } from "./arcPlcElection";
import { ARC_CAP_FRAC, BASE_ACRE_FACTOR, arcCapRate } from "./arcPlcRule";
import { ELECTION_WINDOWS, cropYearLabel } from "./arcPlcSheet";

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));

// ⚠ THE PROGRAM YEAR IS REQUIRED, WITH NO DEFAULT. The election window, the final-MYA dates and
// every FSA row are per year, and a defaulted year is how a page answers quietly for the wrong
// one — the SCO/ECO band rule already records this (a missing year is thrown, never assumed).
function requireYear(programYear) {
  const Y = Number(programYear);
  if (programYear == null || programYear === "" || !Number.isInteger(Y) || Y < 2014 || Y > 2100)
    throw new Error("programYear is required (got " + JSON.stringify(programYear) + ")");
  return Y;
}

// A county NAME as a key: periods and whitespace only — the rule App.jsx's caCountyKey states,
// because arc_co_county writes "ST. LAWRENCE" where sco_eco_county and the book write
// "ST LAWRENCE". An apostrophe is kept (ST MARY'S), exactly as there.
export const countyKey = (v) => String(v == null ? "" : v)
  .toUpperCase().split(".").join("").split(/s+/).filter(Boolean).join(" ");

// ── THE SILAS COUNTY-YIELD SCENARIO — ONE RULE, BOTH SURFACES ──────────────────────────────
// The Silas model's this-year-vs-its-own-normal read for the county (forecast ÷ anchor), applied
// to FSA's benchmark as a SCENARIO for the county's yield. New York corn only — the only crop and
// state the model covers — and only until FSA publishes the county's actual yield, after which
// the published figure is the answer and no scenario is needed. The Coverage Advisor's
// arcYieldFactor calls THIS, so the Advisor's headline and this page's strip cannot disagree.
// It is never the farm's yield, its APH, its FSA payment yield or its harvest.
export function silasYieldFactor({ crop, state, countyRow, mi }) {
  if (String(crop || "").toUpperCase() !== "CORN" || String(state || "").toUpperCase() !== "NY") return null;
  if (!countyRow || countyRow.actual_yield != null) return null;
  const y = num(mi && mi.y), a = num(mi && mi.anchor);
  return y != null && a ? y / a : null;
}

// ── THE FARM'S ROWS — ONE LOADER for the Coverage Advisor's button and the PDF tool ──────────
// `fetchRows(table, { select, filters })` is the caller's REST read (the app's sbFetch, or a
// service-role fetch in tools/arc-plc/farm-summary-pdf.js) — so the page a grower is handed and
// the page an agent prints travel the same executed path. The county read is scoped by crop AND
// state: the FSA feed carries six crops in 48 states.
export const ARC_CO_SELECT = "program_year,crop,state,county,practice,benchmark_yield,benchmark_revenue,guarantee_revenue,actual_yield,actual_revenue,payment_rate,payment_factor";
export async function fetchFarmElectionRows(fetchRows, { farmerId, farmerIds = null, programYear, knownCrops = [] } = {}) {
  // ⚠ A FARM IS NOT ALWAYS ONE `farmers` ROW. The wheat panel groups its farms by NAME and
  // carries `farmerIds` (plural) because this book has minted duplicate rows for one grower
  // before — the CY2027 rollover made five when the new file spelled an insured differently.
  // Measured 2026-09-19: 281 rows, 281 distinct names, so every list is 1 long TODAY. Taking
  // the first id would be right today and silently print half a farm the next time it is not.
  const fIds = (farmerIds && farmerIds.length ? farmerIds : (farmerId != null ? [farmerId] : [])).filter((v) => v != null);
  if (!fIds.length) throw new Error("no farm chosen");
  const Y = requireYear(programYear);
  const pol = await fetchRows("policies", { select: "id,state,crop_year",
    filters: [fIds.length === 1 ? `farmer_id=eq.${fIds[0]}` : `farmer_id=in.(${fIds.join(",")})`, `crop_year=eq.${Y}`] });
  const stBy = {};
  for (const p of pol || []) stBy[p.id] = p.state;
  const ids = Object.keys(stBy);
  const cov = ids.length ? await fetchRows("crop_coverage", { select: "policy_id,crop,county", filters: [`policy_id=in.(${ids.join(",")})`] }) : [];
  const insuredCells = (cov || []).map((c) => ({ crop: c.crop, state: stBy[c.policy_id], county: c.county }));
  const known = new Set((knownCrops || []).map((c) => String(c || "").toUpperCase()));
  const crops = [...new Set(insuredCells.map((c) => String(c.crop || "").toUpperCase()))].filter((c) => known.has(c));
  const states = [...new Set(insuredCells.map((c) => String(c.state || "").toUpperCase()).filter(Boolean))];
  const cropIn = crops.map((c) => '"' + c + '"').join(",");
  const arcRows = crops.length && states.length
    ? await fetchRows("arc_co_county", { select: ARC_CO_SELECT, filters: [`program_year=eq.${Y}`, `crop=in.(${cropIn})`, `state=in.(${states.join(",")})`] })
    : [];
  return { insuredCells, arcRows: arcRows || [] };
}

// The Silas model's county reads, keyed by county: its forecast `y` and its own normal (`anchor`,
// falling back to its 5-year mean). The Coverage Advisor builds its map through this too.
export function miCountyMap(counties) {
  const m = {};
  for (const c of counties || [])
    if (c && c.name && c.y != null) m[countyKey(c.name)] = { y: +c.y, anchor: c.anchor != null ? +c.anchor : (c.y5 != null ? +c.y5 : null) };
  return m;
}

// ── THE WHOLE PAGE FOR ONE FARM AND ONE PROGRAM YEAR — the button and the PDF tool call THIS ──
// Every read carries the program year it was handed: FSA's national figures, the farm's
// policies for that crop year, FSA's county rows, and the Silas model's snapshot. It depends on
// nothing the calling page happened to preload, so the year is the caller's CHOICE and cannot be
// quietly bounded by a page's own fixed list of years. `answersFor(crop, state, county)` returns
// THIS farm's own { irrigatedPct, paymentYield } — the Advisor reads them through its per-farm key.
export async function loadFarmElectionSummary(fetchRows, { farmerId, farmerIds = null, farmerName = null, programYear, answersFor = null, asOf = null, baseAcreCounties = null } = {}) {
  const Y = requireYear(programYear);
  if (!farmerId && !(farmerIds && farmerIds.length)) throw new Error("no farm chosen");
  const refRows = (await fetchRows("arc_plc_reference", { filters: [`program_year=eq.${Y}`] })) || [];
  const { insuredCells, arcRows } = await fetchFarmElectionRows(fetchRows, { farmerId, farmerIds, programYear: Y, knownCrops: refRows.map((r) => r.crop) });
  const snaps = (await fetchRows("mi_model_snapshot", { select: "crop_year,payload", filters: [`crop_year=eq.${Y}`] })) || [];
  let mi = {};
  for (const s of snaps) if (s && Number(s.crop_year) === Y && s.payload && Array.isArray(s.payload.mi_counties)) mi = { ...mi, ...miCountyMap(s.payload.mi_counties) };
  const irrigatedPcts = {}, paymentYields = {};
  if (typeof answersFor === "function") {
    for (const c of insuredCells) {
      const cr = String(c.crop || "").toUpperCase(), st = String(c.state || "").toUpperCase(), co = countyKey(c.county);
      if (!cr || !st || !co) continue;
      const a = answersFor(cr, st, co) || {}, k = cr + "|" + st + "|" + co;
      if (num(a.irrigatedPct) != null) irrigatedPcts[k] = num(a.irrigatedPct);
      if (num(a.paymentYield) != null && num(a.paymentYield) > 0) paymentYields[k] = num(a.paymentYield);
    }
  }
  const inp = farmElectionInputs({ insuredCells, arcRows, refRows, mi, programYear: Y, irrigatedPcts });
  // `baseAcreCounties` comes from the farm's own FSA-156EZ for THIS program year (src/fsa156ez.js
  // baseAcreCountiesOf — the tracts' physical locations). Without one, every county result this
  // loader produces is an insurance-county scenario, exactly as before.
  return buildFarmElectionSummary({ farmerName, ...inp, paymentYields, irrigatedPcts, asOf, cellSource: "the farm's " + Y + " crop insurance", baseAcreCounties });
}

// ── WHICH CROPS AND COUNTIES — from the farm's own crop insurance ──────────────────────────
// The summary is per FARMER, so it needs the farm's covered commodities by county. The source is
// the farm's insured crops for the program year (crop × state × county), intersected with the
// crops FSA published ARC/PLC figures for — never a hard-coded corn/soy/wheat list. An insured
// crop FSA's loaded files carry no figures for is NAMED as not on the page, not dropped.
// `mi` is { COUNTYKEY: { y, anchor } } — the Silas model's county reads for the program year.
export function farmElectionInputs({ insuredCells = [], arcRows = [], refRows = [], mi = {}, programYear, irrigatedPcts = {} }) {
  const Y = requireYear(programYear);
  const refs = {};
  for (const r of refRows || []) if (r && Number(r.program_year) === Y && r.crop) refs[String(r.crop).toUpperCase()] = r;
  const cellKey = (crop, state, county) => [String(crop || "").toUpperCase(), String(state || "").toUpperCase(), countyKey(county)].join("|");
  const practice = {};
  for (const r of arcRows || []) {
    if (!r || Number(r.program_year) !== Y) continue;
    const k = cellKey(r.crop, r.state, r.county);
    (practice[k] = practice[k] || {})[String(r.practice || "ALL").toUpperCase()] = r;
  }
  const seen = new Set(), cells = [], silasFactors = {}, off = new Map(), noState = new Map();
  for (const c of insuredCells || []) {
    const crop = String(c && c.crop || "").toUpperCase(), state = String(c && c.state || "").toUpperCase(), county = countyKey(c && c.county);
    if (!crop || !county) continue;
    // A coverage line whose policy records no state cannot be joined to FSA's county row — the
    // same county name exists in several states. It is NAMED, never guessed into a state.
    if (!state) { if (!noState.has(crop)) noState.set(crop, new Set()); noState.get(crop).add(county); continue; }
    const k = cellKey(crop, state, county);
    if (seen.has(k)) continue;
    seen.add(k);
    if (!refs[crop]) { if (!off.has(crop)) off.set(crop, []); off.get(crop).push(county); continue; }
    cells.push({ crop, state, county, practiceRows: practice[k] || null });
    const res = resolvePracticeRow(practice[k] || null, irrigatedPcts[k] ?? null);
    const f = silasYieldFactor({ crop, state, countyRow: res.row, mi: mi[county] });
    if (f != null) silasFactors[k] = f;
  }
  const notCovered = [
    ...[...off.entries()].map(([crop, counties]) => ({ crop, counties: counties.sort(), reason: "no FSA ARC/PLC figures are loaded for this crop" })),
    ...[...noState.entries()].map(([crop, counties]) => ({ crop, counties: [...counties].sort(), reason: "the policy records no state, so FSA's county row cannot be matched" })),
  ].sort((a, b) => a.crop.localeCompare(b.crop) || a.reason.localeCompare(b.reason));
  return { programYear: Y, cells, refs, silasFactors, notCovered };
}

// The covered commodities, in the order a WNY grower reads them. Anything else FSA publishes and
// the farm grows follows, alphabetically — the page is never limited to these three.
export const CROP_ORDER = ["CORN", "SOYBEANS", "WHEAT"];

// County yield as a share of its own FSA benchmark: short, normal, long. The Silas county-yield
// scenario (a model's this-year-vs-normal ratio for the county, where one exists) is added
// between them — labelled as a SCENARIO for the COUNTY, never as the farm's yield.
export const SUMMARY_YIELD_FACTORS = [0.85, 1, 1.15];

// When USDA publishes the FINAL marketing-year average for each crop's 2026/27 year — FSA's
// program-data table as Kevin cited it on 2026-09-19. Until then every MYA on the page is a
// projection and says so.
export const MYA_FINAL_PUBLICATION = {
  2026: { CORN: "2027-09-30", SOYBEANS: "2027-09-30", WHEAT: "2027-06-30" },
};

// FSA's own ARC-CO identities, checked on every cell rather than assumed: benchmark revenue is
// benchmark price × benchmark yield, and the guarantee is 90% of it. A cell that does not
// reconcile is FLAGGED on the page, never silently repaired.
const GUARANTEE_FRAC = 0.9;
export function reconcileCell(benchmarkPrice, row) {
  const bp = num(benchmarkPrice), by = num(row && row.benchmark_yield), br = num(row && row.benchmark_revenue),
        g = num(row && row.guarantee_revenue);
  const out = { revenueDelta: null, guaranteeDelta: null, ok: null };
  if (bp != null && by != null && br != null) out.revenueDelta = Math.round((bp * by - br) * 100) / 100;
  if (br != null && g != null) out.guaranteeDelta = Math.round((GUARANTEE_FRAC * br - g) * 100) / 100;
  // A cent of rounding either way is FSA's own two-decimal publishing, not a disagreement.
  const within = (d) => d != null && Math.abs(d) <= 0.01;
  out.ok = out.revenueDelta != null && out.guaranteeDelta != null ? within(out.revenueDelta) && within(out.guaranteeDelta) : null;
  return out;
}

// The figures a grower should be able to check against FSA, named so no two can be confused.
// `arcPriceTriggerAtBenchmarkYield` is guarantee ÷ benchmark yield — the price below which
// ARC-CO pays IF the county harvests exactly its benchmark. It is NOT a reference price, and the
// page never calls it one (Kevin: $5.03, $4.53 and $4.42 must be told apart without guessing).
export function keyNumbers({ ref, row }) {
  const bp = num(ref && ref.arc_benchmark_price), by = num(row && row.benchmark_yield),
        br = num(row && row.benchmark_revenue), g = num(row && row.guarantee_revenue);
  const capRate = arcCapRate(br);
  return {
    arcBenchmarkPrice: bp,
    arcBenchmarkYield: by,
    arcBenchmarkRevenue: br,
    arcGuarantee: g,
    arcPriceTriggerAtBenchmarkYield: g != null && by ? g / by : null,
    plcEffectiveReferencePrice: num(ref && ref.effective_ref_price),
    // FSA's national loan rate: the floor under the price both programs pay on (arcPlcRule programPrice).
    nationalLoanRate: num(ref && ref.national_loan_rate),
    myaProjection: num(ref && ref.mya_price),
    myaStatus: (ref && ref.mya_status) || null,
    myaAsOf: (ref && ref.source_asof) || null,
    arcMaxPaymentRate: capRate,                                             // $/acre, FSA's published 12% cap, to the cent
    arcMaxPerBaseAcre: capRate != null ? capRate * BASE_ACRE_FACTOR : null, // after the 85% payment-acre factor
    reconcile: reconcileCell(bp, row),
  };
}

// The comparison at ONE county yield, in the shape the page prints. Everything is enrolRead's
// (the Coverage Advisor's and the election sheet's call) plus countyRead at USDA's projection.
function comparisonAt({ row, ref, factor, paymentYield }) {
  const e = enrolRead({ countyRow: row, ref, paymentYield, yieldFactor: factor });
  if (!e || !e.usable) return { usable: false, reason: (e && e.refused) || "the county's published benchmark is not loaded" };
  const atProjection = countyRead({ countyRow: row, ref, yieldFactor: factor });
  const kind = num(e.switchYield) != null ? "threshold" : e.arcFirst ? "arcFirst" : "none";
  return {
    usable: true, factor: num(e.yieldFactor), countyYield: num(e.scenarioYield),
    kind,
    switchYield: num(e.switchYield),
    arcStartsBelow: num(e.arcTriggerPrice),
    plcStartsBelow: num(e.plcTriggerPrice),
    arcMaxedBelow: num(e.capPrice),
    // Ruler marks where no single threshold exists: the price below which PLC's modeled payment
    // is larger, at each payment yield. The ruler is the county benchmark's 60/80/100%.
    lines: kind === "arcFirst" ? (e.lines || []).map((l) => ({ paymentYield: l.paymentYield, plcLargerBelow: l.below })) : [],
    // The farm's own payment yield, where one is known: its line, and the lean it gives.
    paymentYield: num(e.paymentYield),
    plcLargerBelowAtPaymentYield: num(e.plcBelow),
    // Below the switch, the prices where ARC-CO's modeled payment leads, at the farm's own payment yield.
    arcWinsBetween: Array.isArray(e.arcWinsBetween) ? e.arcWinsBetween.map(num) : null,
    // The ONE qualified below-the-switch statement every surface prints, and — where the farm's
    // payment yield is known — the same statement with the band of prices it holds over.
    belowSwitch: BELOW_SWITCH_ARC,
    belowSwitchAtPaymentYield: num(e.paymentYield) != null ? belowSwitchArc(e.arcWinsBetween) : null,
    lean: e.lean || null,
    reason: kind === "none" ? (e.switchReason || null) : null,
    // At USDA's projection: what each program's modeled payment is, per base acre (ARC-CO) and
    // per bushel of payment yield (PLC — it needs the payment yield to become dollars).
    arcPerBaseAcreAtProjection: atProjection.usable ? num(atProjection.arcPerAcre) : null,
    plcRateAtProjection: atProjection.usable ? num(atProjection.plcRate) : null,
    arcCappedAtProjection: !!(atProjection.usable && atProjection.capped),
  };
}

// One county of one crop.
export function countyCell({ crop, state, county, practiceRows, irrigatedPct = null, ref, paymentYield = null, silasFactor = null }) {
  const base = { crop, state, county };
  if (!ref) return { ...base, usable: false, reason: "FSA has not published this crop's national figures for the program year" };
  const res = resolvePracticeRow(practiceRows, irrigatedPct);
  // ⚠ A COUNTY FSA SPLITS BY IRRIGATION, WITH NO ANSWER FOR THIS FARM, IS NOT COMPUTED. Both
  // published rows are carried so the page can show what the answer is worth (BUG-092 stays
  // closed: nothing is picked for the farm).
  if (!res.row) return { ...base, usable: false, reason: res.refused || "no county figures published",
                         split: !!res.split,
                         irrigatedBenchmarkYield: res.irrigated ? num(res.irrigated.benchmark_yield) : null,
                         nonIrrigatedBenchmarkYield: res.nonIrrigated ? num(res.nonIrrigated.benchmark_yield) : null };
  const row = res.row;
  const head = comparisonAt({ row, ref, factor: null, paymentYield });
  const sf = num(silasFactor);
  const factors = [...SUMMARY_YIELD_FACTORS.map((f) => ({ factor: f, kind: f === 1 ? "benchmark" : "fixed" })),
                   ...(sf != null && sf > 0 ? [{ factor: sf, kind: "silas" }] : [])]
    .sort((a, b) => a.factor - b.factor);
  const strip = factors.map(({ factor, kind }) => ({ ...comparisonAt({ row, ref, factor, paymentYield }), scenario: kind }));
  return {
    ...base, usable: true, practice: res.practice, blended: !!res.blended, irrigatedPct: res.irrigatedPct ?? null,
    keyNumbers: keyNumbers({ ref, row }),
    comparison: head,
    countyYieldSensitivity: strip,
    silasFactor: sf,
    // FSA's own row, carried so a crop-wide ruler can be priced through the engine for this county.
    countyRow: row,
  };
}

// ── ONE RULER PER CROP ─────────────────────────────────────────────────────────────────────
// Where ARC-CO pays first, the page shows the price line below which PLC's modeled payment is
// larger, at a few ruler payment yields. The engine's own ruler is 60 / 80 / 100% of ONE
// county's benchmark, so three counties would print three different rulers (Ontario corn's
// 105/140/175 beside Livingston's 120/160/200) and three tables. The crop gets ONE set of marks —
// the engine's ruler for its largest-benchmark county — and every county is priced at those same
// marks by the engine (enrolRead at that payment yield: the SAME plcPaysMoreBelow the ruler uses).
// The marks are marks on a ruler, never a yield of the farm's.
function cropRuler(counties, ref) {
  const arc = counties.filter((k) => k.usable && k.comparison && k.comparison.usable && k.comparison.kind === "arcFirst");
  if (!arc.length) return null;
  const top = arc.slice().sort((a, b) => num(b.keyNumbers.arcBenchmarkYield) - num(a.keyNumbers.arcBenchmarkYield))[0];
  const marks = top.comparison.lines.map((l) => l.paymentYield).filter((y) => num(y) != null);
  if (!marks.length) return null;
  return {
    marks,
    basisCounty: top.county,
    counties: arc.map((k) => ({
      county: k.county,
      lines: marks.map((m) => {
        const e = enrolRead({ countyRow: k.countyRow, ref, paymentYield: m });
        return { paymentYield: m, plcLargerBelow: e && e.usable ? num(e.plcBelow) : null };
      }),
    })),
  };
}

// The whole farm. `cells` are the farm's crop × state × county cells for the program year, each
// with FSA's practice rows. `refs` is { CROP: arc_plc_reference row }. `paymentYields` and
// `irrigatedPcts` are keyed `${crop}|${state}|${county}` and are OPTIONAL — only what the agent
// has typed for THIS farm; nothing is defaulted, estimated or borrowed from another farm.
// ⚠ WHOSE COUNTY IS IT? (Kevin, 2026-09-19.) ARC-CO is tied to the physical location of the FSA
// farm's BASE ACRES, and FSA handles a farm spanning counties under its own rules. Silas holds no
// FSA farm / base-acre record, so the county each result is computed for is the county the crop is
// INSURED in — an INSURANCE-COUNTY SCENARIO, never the FSA farm election result, however exactly its
// arithmetic reconciles to FSA's workbook (the right formula on the wrong county still reconciles).
// `baseAcreCounties` — { "CROP|ST": ["COUNTY", …] } read from an FSA farm record — is the one seam that
// may mark a county result "fsa". The Advisor supplies it from an uploaded FSA-156EZ (sql/110); without one, every result is "insurance".
export const COUNTY_BASIS = { FSA: "fsa", INSURANCE: "insurance" };
export function countyBasisOf(baseAcreCounties, crop, state, county) {
  const list = baseAcreCounties && baseAcreCounties[String(crop || "").toUpperCase() + "|" + String(state || "").toUpperCase()];
  const fsa = Array.isArray(list) ? list.map((c) => String(c || "").toUpperCase()).filter(Boolean) : null;
  return { countyBasis: fsa && fsa.includes(String(county || "").toUpperCase()) ? COUNTY_BASIS.FSA : COUNTY_BASIS.INSURANCE,
           fsaBaseAcreCounties: fsa };
}
export function buildFarmElectionSummary({ farmerName, programYear, cells = [], refs = {},
                                           paymentYields = {}, irrigatedPcts = {}, silasFactors = {}, asOf = null,
                                           notCovered = [], cellSource = null, baseAcreCounties = null }) {
  const Y = requireYear(programYear);
  const key = (c) => [c.crop, c.state, c.county].map((v) => String(v || "").toUpperCase()).join("|");
  const byCrop = new Map();
  for (const c of cells) {
    const crop = String(c.crop || "").toUpperCase();
    if (!byCrop.has(crop)) byCrop.set(crop, []);
    byCrop.get(crop).push({ ...countyCell({
      crop, state: c.state, county: c.county, practiceRows: c.practiceRows,
      irrigatedPct: irrigatedPcts[key(c)] ?? null, ref: refs[crop] || null,
      paymentYield: num(paymentYields[key(c)]), silasFactor: num(silasFactors[key(c)]),
    }), ...countyBasisOf(baseAcreCounties, crop, c.state, c.county) });
  }
  const rank = (c) => { const i = CROP_ORDER.indexOf(c); return i < 0 ? 100 : i; };
  const crops = [...byCrop.keys()].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b)).map((crop) => ({
    crop,
    label: cropYearLabel(crop, Y),
    myaFinalPublication: (MYA_FINAL_PUBLICATION[Y] || {})[crop] || null,
    counties: byCrop.get(crop).sort((a, b) => String(a.county).localeCompare(String(b.county))),
  })).map((c) => ({ ...c, ruler: cropRuler(c.counties, refs[c.crop] || null) }));
  const counties = [...new Set(cells.map((c) => String(c.county || "").toUpperCase()).filter(Boolean))].sort();
  const asOfs = Object.values(refs).map((r) => r && r.source_asof).filter(Boolean).sort();
  // The page is an FSA result only if EVERY county on it is an FSA base-acre county.
  const allCells = crops.flatMap((c) => c.counties);
  const countyAuthority = allCells.length && allCells.every((k) => k.countyBasis === COUNTY_BASIS.FSA) ? COUNTY_BASIS.FSA : "insurance-scenario";
  // Where an FSA record IS known and disagrees with the insured counties, say so by name.
  const baseAcreMismatch = [];
  for (const c of crops) {
    const byState = new Map();
    for (const k of c.counties) if (k.fsaBaseAcreCounties) byState.set(String(k.state || "").toUpperCase(), { fsa: k.fsaBaseAcreCounties, insured: [] });
    for (const k of c.counties) { const g = byState.get(String(k.state || "").toUpperCase()); if (g) g.insured.push(String(k.county).toUpperCase()); }
    for (const [state, g] of byState) {
      const notFsa = g.insured.filter((x) => !g.fsa.includes(x)), notInsured = g.fsa.filter((x) => !g.insured.includes(x));
      if (notFsa.length || notInsured.length) baseAcreMismatch.push({ crop: c.crop, state, insured: g.insured, fsa: g.fsa, notFsa, notInsured });
    }
  }
  return {
    farmerName: farmerName || null, programYear: Y, counties,
    window: ELECTION_WINDOWS[Y] || null, nextWindow: ELECTION_WINDOWS[Y + 1] || null,
    fsaAsOf: asOfs.length ? asOfs[asOfs.length - 1] : null,
    asOf: asOf || new Date().toISOString().slice(0, 10),
    // The program constants the page states in words — the engine's, not the renderer's.
    rules: { guaranteeFrac: GUARANTEE_FRAC, capFrac: ARC_CAP_FRAC, baseAcreFactor: BASE_ACRE_FACTOR },
    // Where the crop list came from, and what the farm grows that this page cannot speak for.
    cellSource: cellSource || null,
    // Whose counties these are: "fsa" only where an FSA base-acre record names every county.
    countyAuthority, baseAcreMismatch,
    notCovered: (notCovered || []).map((x) => ({ crop: String(x.crop || "").toUpperCase(), counties: [...(x.counties || [])], reason: x.reason || null })),
    crops,
  };
}
